import { createAsyncThunk } from '@reduxjs/toolkit'
import { getExceptionPayload } from '../../../lib/utils/get-exception-payload'
import {
  SavedContactsService,
  CreateContactRequest,
  UpdateContactRequest,
  FindContactRequest,
} from '../../../services/api/saved-contacts.service'
import { ApiRequestErrorType } from '../../../types/api/api.types'

const savedContactsService = new SavedContactsService()

/**
 * Get all saved contacts
 */
export const getSavedContactsThunk = createAsyncThunk(
  'saved-contacts/get-all',
  async (
    { token, limit, sort }: { token: string; limit?: number; sort?: 'most_active' | 'recent' },
    { rejectWithValue },
  ) => {
    try {
      const response = await savedContactsService.getContacts(token, limit, sort)
      return response?.data
    } catch (ex) {
      return rejectWithValue(getExceptionPayload(ex) as ApiRequestErrorType)
    }
  },
)

/**
 * Get a single contact by ID
 */
export const getSavedContactThunk = createAsyncThunk(
  'saved-contacts/get-single',
  async ({ contactId, token }: { contactId: number; token: string }, { rejectWithValue }) => {
    try {
      const response = await savedContactsService.getContact(contactId, token)
      return response?.data
    } catch (ex) {
      return rejectWithValue(getExceptionPayload(ex) as ApiRequestErrorType)
    }
  },
)

/**
 * Create a new saved contact
 */
export const createSavedContactThunk = createAsyncThunk(
  'saved-contacts/create',
  async ({ data, token }: { data: CreateContactRequest; token: string }, { rejectWithValue }) => {
    try {
      const response = await savedContactsService.createContact(data, token)
      return response?.data
    } catch (ex) {
      return rejectWithValue(getExceptionPayload(ex) as ApiRequestErrorType)
    }
  },
)

/**
 * Update an existing contact
 */
export const updateSavedContactThunk = createAsyncThunk(
  'saved-contacts/update',
  async (
    { contactId, data, token }: { contactId: number; data: UpdateContactRequest; token: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await savedContactsService.updateContact(contactId, data, token)
      return response?.data
    } catch (ex) {
      return rejectWithValue(getExceptionPayload(ex) as ApiRequestErrorType)
    }
  },
)

/**
 * Delete a contact
 */
export const deleteSavedContactThunk = createAsyncThunk(
  'saved-contacts/delete',
  async ({ contactId, token }: { contactId: number; token: string }, { rejectWithValue }) => {
    try {
      const response = await savedContactsService.deleteContact(contactId, token)
      return response?.data
    } catch (ex) {
      return rejectWithValue(getExceptionPayload(ex) as ApiRequestErrorType)
    }
  },
)

/**
 * Increment usage count (after successful payment)
 */
export const incrementContactUsageThunk = createAsyncThunk(
  'saved-contacts/increment-usage',
  async ({ contactId, token }: { contactId: number; token: string }, { rejectWithValue }) => {
    try {
      const response = await savedContactsService.incrementUsage(contactId, token)
      return response?.data
    } catch (ex) {
      return rejectWithValue(getExceptionPayload(ex) as ApiRequestErrorType)
    }
  },
)

/**
 * Find a contact by phone and payment method
 */
export const findSavedContactThunk = createAsyncThunk(
  'saved-contacts/find',
  async ({ data, token }: { data: FindContactRequest; token: string }, { rejectWithValue }) => {
    try {
      const response = await savedContactsService.findContact(data, token)
      return response?.data
    } catch (ex) {
      return rejectWithValue(getExceptionPayload(ex) as ApiRequestErrorType)
    }
  },
)
